import { Button } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useLocation } from 'react-router-dom'
import { ROUTES } from '../../../helpers/routes-helper'
import useAppUtilities from '../../../hooks/useAppUtilities'
import { debugLog, errorLog } from '../../../utils/log-helper'
import {
  ErrorContainer,
  ErrorDescriptionTypography,
  ErrorMainContainer,
  ErrorTitleTypography,
} from './styled/Error.styled'

interface ErrorData {
  title: string
  heading: string
  desc: string
  navigatePath: string
  navigateText: string
}
interface ErrorPageData {
  unauthorized: ErrorData
  '404': ErrorData
}
interface ErrorProps {
  type: keyof ErrorPageData
}

// A dropped session can bounce a user here repeatedly (AuthGuard → /unauthorized → login → callback
// → AuthGuard …). Auto-redirect at most once per short window; after that show a manual CTA so the
// user is never trapped in a redirect loop.
const RELOGIN_GUARD_KEY = 'rsly_unauth_relogin_at'
const RELOGIN_GUARD_MS = 20_000

const Error: React.FC<ErrorProps> = ({ type }) => {
  const { navigate } = useAppUtilities()
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0()
  const location = useLocation()

  const errData = useMemo(() => errorData[type], [type])

  // Where the user was headed before AuthGuard bounced them here (falls back to home, never the old
  // hardcoded points page).
  const returnTo = useMemo(() => {
    const from = (location.state as { from?: string } | null)?.from
    return from && from.startsWith('/') ? from : ROUTES.HOME
  }, [location.state])

  const recentlyRedirected = (): boolean => {
    try {
      const at = Number(sessionStorage.getItem(RELOGIN_GUARD_KEY) ?? '0')
      return at > 0 && Date.now() - at < RELOGIN_GUARD_MS
    } catch {
      return false
    }
  }

  const startReLogin = (): void => {
    try {
      sessionStorage.setItem(RELOGIN_GUARD_KEY, String(Date.now()))
    } catch {
      // sessionStorage unavailable — the debounce in the auth-failure handler still guards loops.
    }
    void loginWithRedirect({ appState: { returnTo } }).catch((e) => {
      errorLog('[Error] loginWithRedirect failed', e)
    })
  }

  useEffect(() => {
    document.title = `Resactly | ${errData.title}`
    debugLog('errData: ', errData)
  }, [errData.title])

  useEffect(() => {
    if (type !== 'unauthorized') return
    if (isLoading) return
    if (isAuthenticated) return
    // Only auto-redirect once per window; on a loop, fall through to the manual CTA below.
    if (recentlyRedirected()) return
    startReLogin()
  }, [type, isLoading, isAuthenticated])

  // While we're auto-redirecting to Auth0 (first drop), render nothing to avoid a flash of the wall.
  if (type === 'unauthorized' && !isLoading && !isAuthenticated && !recentlyRedirected()) {
    return null
  }

  // Session-expired state: a friendly, loop-safe re-login prompt instead of a bare "Unauthorized".
  if (type === 'unauthorized') {
    return (
      <ErrorContainer>
        <ErrorMainContainer>
          <ErrorTitleTypography>You&apos;ve been signed out</ErrorTitleTypography>
          <ErrorDescriptionTypography>
            Your session ended. Sign back in to pick up where you left off.
          </ErrorDescriptionTypography>
          <Button variant="contained" onClick={startReLogin}>
            Sign back in
          </Button>
          <Button variant="text" onClick={() => navigate(ROUTES.HOME)}>
            Go to home
          </Button>
        </ErrorMainContainer>
      </ErrorContainer>
    )
  }

  return (
    <ErrorContainer>
      <ErrorMainContainer>
        <ErrorTitleTypography>{errData.heading}</ErrorTitleTypography>
        <ErrorDescriptionTypography>{errData.desc}</ErrorDescriptionTypography>
        <Button variant="outlined" onClick={() => navigate(errData.navigatePath)}>
          {errData.navigateText}
        </Button>
      </ErrorMainContainer>
    </ErrorContainer>
  )
}

export default Error

const errorData: ErrorPageData = {
  unauthorized: {
    title: 'Signed Out',
    heading: 'You’ve been signed out',
    desc: 'Your session ended. Sign back in to continue.',
    navigatePath: ROUTES.HOME,
    navigateText: 'Home',
  },
  '404': {
    title: 'Page Not Found',
    heading: 'Oops 404!',
    desc: 'Page Not Found',
    navigatePath: ROUTES.HOME,
    navigateText: 'Home',
  },
}